<template>
  <div v-loading="loading">
    <div class="header">
      <div class="filters">
        <el-radio-group v-model="filterSectorType" class="sector-group" @change="onSectorChange">
          <el-radio-button v-for="(label, val) in SECTOR_LABELS" :key="val" :value="val">
            {{ label }}
          </el-radio-button>
        </el-radio-group>

        <el-select
          ref="periodSelectRef"
          v-model="filterPeriodType"
          clearable
          placeholder="Davr turi"
          style="width: 130px"
          :disabled="!filterSectorType"
          @change="onPeriodChange"
          @clear="onPeriodChange"
        >
          <el-option
            v-for="opt in periodOptions"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
          />
        </el-select>

        <el-select
          v-if="filterPeriodType === 'YEARLY'"
          v-model="filterYear"
          clearable
          placeholder="Yil"
          style="width: 100px"
          @change="resetPage"
          @clear="resetPage"
        >
          <el-option v-for="y in yearOptions" :key="y" :label="y" :value="y" />
        </el-select>

        <el-select
          v-if="filterPeriodType === 'QUARTERLY'"
          v-model="filterQuarter"
          clearable
          placeholder="Chorak"
          style="width: 110px"
          @change="resetPage"
          @clear="resetPage"
        >
          <el-option v-for="q in [1, 2, 3, 4]" :key="q" :label="`${q}-chorak`" :value="q" />
        </el-select>

        <el-select
          v-if="filterPeriodType === 'MONTHLY'"
          v-model="filterMonth"
          clearable
          placeholder="Oy"
          style="width: 110px"
          @change="resetPage"
          @clear="resetPage"
        >
          <el-option v-for="m in 12" :key="m" :label="`${m}-oy`" :value="m" />
        </el-select>

        <el-button @click="resetFilters">Tozalash</el-button>
      </div>

      <el-button type="primary" @click="$router.push({ name: 'GfsDataAdd' })">
        Qo'shish
      </el-button>
    </div>

    <el-table class="table-body" :data="tableData" border height="calc(100vh - 250px)">
      <el-table-column type="index" label="T/r" width="60" />
      <el-table-column label="Sektor turi" min-width="200">
        <template #default="scope">
          {{ SECTOR_LABELS[scope.row.sector_type] || scope.row.sector_type }}
        </template>
      </el-table-column>
      <el-table-column label="Davr turi" width="130">
        <template #default="scope">
          {{ PERIOD_LABELS[scope.row.period_type] || scope.row.period_type }}
        </template>
      </el-table-column>
      <el-table-column prop="year" label="Yil" width="80" align="center" />
      <el-table-column label="Chorak/Oy" width="120" align="center">
        <template #default="scope">
          <span v-if="scope.row.quarter">{{ scope.row.quarter }}-chorak</span>
          <span v-else-if="scope.row.month">{{ scope.row.month }}-oy</span>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column label="Fayl" min-width="180">
        <template #default="scope">
          <el-image
            v-if="isImage(scope.row)"
            style="height: 60px"
            :src="BASE_FILE_URL + scope.row.file_id"
            fit="contain"
          />
          <span v-else>{{ scope.row.file_name || '—' }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="file_extension" label="Format" width="90" align="center">
        <template #default="scope">
          {{ scope.row.file_extension || '—' }}
        </template>
      </el-table-column>
      <el-table-column label="Sana" width="130" align="center">
        <template #default="scope">
          {{ formatDate(scope.row.created_at) }}
        </template>
      </el-table-column>
      <el-table-column label="Amallar" width="175" align="center">
        <template #default="scope">
          <el-button
            size="small"
            type="primary"
            @click="$router.push({ name: 'GfsDataEdit', params: { id: scope.row.id } })"
          >
            Tahrirlash
          </el-button>
          <el-button size="small" type="danger" @click="deleteItem(scope.row.id)">
            O'chirish
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <div class="pagination">
      <div>Umumiy soni: {{ totalCount }}</div>
      <el-pagination
        background
        layout="sizes, prev, pager, next"
        :total="totalCount"
        v-model:page-size="limit"
        v-model:current-page="currentPage"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import http from "@/http";
import { useRouter, useRoute } from "vue-router";

const router = useRouter();
const route = useRoute();

const periodSelectRef = ref(null);
const loading = ref(false);
const tableData = ref([]);
const totalCount = ref(0);
const limit = ref(20);
const currentPage = ref(1);

const filterSectorType = ref("GENERAL_GOVERNMENT");
const filterPeriodType = ref("");
const filterYear = ref("");
const filterQuarter = ref("");
const filterMonth = ref("");

const BASE_FILE_URL = process.env.VUE_APP_BASE_API_URL + "api/v2/info/file/";
const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "svg", "webp"]);

const isImage = (row) =>
  row.file_content_type?.startsWith("image/") ||
  IMAGE_EXTENSIONS.has(row.file_extension?.toLowerCase());

const SECTOR_LABELS = {
  GENERAL_GOVERNMENT: "Davlat boshqaruv sektori",
  CENTRAL_GOVERNMENT: "Markaziy hukumat sektori",
};
const PERIOD_LABELS = {
  YEARLY: "Yillik",
  QUARTERLY: "Choraklik",
  MONTHLY: "Oylik",
};

const yearOptions = computed(() => {
  const current = new Date().getFullYear();
  return Array.from({ length: current - 2019 }, (_, i) => current - i);
});

const periodOptions = computed(() => {
  if (filterSectorType.value === "GENERAL_GOVERNMENT")
    return [
      { value: "YEARLY", label: "Yillik" },
      { value: "QUARTERLY", label: "Choraklik" },
    ];
  if (filterSectorType.value === "CENTRAL_GOVERNMENT")
    return [{ value: "MONTHLY", label: "Oylik" }];
  return Object.entries(PERIOD_LABELS).map(([value, label]) => ({ value, label }));
});

const formatDate = (val) =>
  val ? new Date(val).toLocaleDateString("uz-UZ") : "—";

const resetPage = () => {
  currentPage.value = 1;
  router.replace({
    query: {
      ...route.query,
      page: 1,
      sector_type: filterSectorType.value || undefined,
      period_type: filterPeriodType.value || undefined,
      year: filterYear.value || undefined,
      quarter: filterQuarter.value || undefined,
      month: filterMonth.value || undefined,
    },
  });
  fetchList();
};

const onSectorChange = () => {
  filterPeriodType.value = "";
  filterQuarter.value = "";
  filterMonth.value = "";
  resetPage();
};

const onPeriodChange = () => {
  filterQuarter.value = "";
  filterMonth.value = "";
  nextTick(() => periodSelectRef.value?.blur());
  resetPage();
};

const resetFilters = () => {
  filterSectorType.value = "GENERAL_GOVERNMENT";
  filterPeriodType.value = "";
  filterYear.value = "";
  filterQuarter.value = "";
  filterMonth.value = "";
  currentPage.value = 1;
  router.replace({ query: { size: limit.value } });
  fetchList();
};

const fetchList = () => {
  loading.value = true;
  const params = {
    limit: limit.value,
    offset: limit.value * (currentPage.value - 1),
  };
  if (filterSectorType.value) params.sector_type = filterSectorType.value;
  if (filterPeriodType.value) params.period_type = filterPeriodType.value;
  if (filterYear.value) params.year = filterYear.value;
  if (filterQuarter.value) params.quarter = filterQuarter.value;
  if (filterMonth.value) params.month = filterMonth.value;

  http
    .get("api/v1/gfs-data", { params })
    .then(({ data }) => {
      tableData.value = data.result;
      totalCount.value = data.count;
    })
    .catch(({ response }) => {
      ElNotification.error({
        message:
          response?.data?.message || "Ma'lumot yuklashda xatolik sodir bo'ldi!",
      });
    })
    .finally(() => {
      loading.value = false;
    });
};

const deleteItem = (id) => {
  ElMessageBox.confirm("GFS ma'lumotni o'chirasizmi?", "Diqqat!", {
    confirmButtonText: "Ha",
    cancelButtonText: "Bekor qilish",
    type: "warning",
  })
    .then(() => {
      loading.value = true;
      http
        .delete(`api/v1/gfs-data/${id}`)
        .then(() => {
          ElMessage({ type: "success", message: "Muvaffaqqiyatli o'chirildi" });
          fetchList();
        })
        .catch(() => {
          ElNotification({
            type: "error",
            title: "Xatolik",
            message: "Iltimos, qayta urinib ko'ring",
          });
        })
        .finally(() => {
          loading.value = false;
        });
    })
    .catch(() => {
      ElMessage({ type: "info", message: "O'chirish bekor qilindi" });
    });
};

const handleCurrentChange = () => {
  router.replace({ query: { ...route.query, page: currentPage.value } });
  fetchList();
};

const handleSizeChange = () => {
  currentPage.value = 1;
  router.replace({ query: { ...route.query, size: limit.value, page: 1 } });
  fetchList();
};

onMounted(() => {
  if (route.query.size) limit.value = parseInt(route.query.size);
  if (route.query.page) currentPage.value = parseInt(route.query.page);
  if (route.query.sector_type) filterSectorType.value = route.query.sector_type;
  if (route.query.period_type) filterPeriodType.value = route.query.period_type;
  if (route.query.year) filterYear.value = route.query.year;
  if (route.query.quarter) filterQuarter.value = parseInt(route.query.quarter);
  if (route.query.month) filterMonth.value = parseInt(route.query.month);
  fetchList();
});
</script>

<style scoped lang="scss">
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  overflow-x: auto;

  .filters {
    display: flex;
    flex-shrink: 0;
    gap: 8px;
    align-items: center;
  }

  .sector-group {
    display: flex;
    flex-wrap: nowrap;

    :deep(.el-radio-button__inner) {
      white-space: nowrap;
    }
  }
}
.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}
</style>
